import {
  AlertCircle,
  Bot,
  BrainCircuit,
  CheckCircle2,
  Clock,
  Cloud,
  ImageIcon,
  ListTodo,
  Loader2,
  type LucideIcon,
  MoreHorizontal,
  Paperclip,
  Pause,
  Play,
  Plus,
  Search,
  Send,
  Sparkles,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleDriveBrowser } from "@/components/google-drive/GoogleDriveBrowser";
import { PageHeader, useToast } from "@/components/shared";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import { SharePointBrowser } from "@/components/sharepoint/SharePointBrowser";
import { getRuntimeConfig } from "@/config/runtime";
import { ALLOWED_IMAGE_TYPES } from "@/constants/files";
import { useMenuPosition } from "@/hooks/useMenuPosition";
import { agentService, taskService } from "@/lib/services";
import type { Agent, TaskStatus, TaskSummary } from "@/types";

const EXAMPLE_GOALS = [
  "Research the top 5 competitors in our space and produce a comparison matrix of features, pricing, and market positioning",
  "Analyze last quarter's support tickets, identify the top recurring issues, and draft an action plan to reduce ticket volume",
  "Write a technical RFC for migrating our auth system to OAuth 2.0, including risks, timeline, and rollback strategy",
];

const statusConfig: Record<TaskStatus, { bg: string; color: string; icon: LucideIcon; label: string }> = {
  pending: { bg: "bg-surface-tertiary/20", color: "text-content-tertiary", icon: Clock, label: "Pending" },
  planning: { bg: "bg-blue-500/20", color: "text-blue-400", icon: BrainCircuit, label: "Planning" },
  running: { bg: "bg-blue-500/20", color: "text-blue-400", icon: Loader2, label: "Running" },
  paused: { bg: "bg-yellow-500/20", color: "text-yellow-400", icon: Pause, label: "Paused" },
  waiting_for_input: {
    bg: "bg-yellow-500/20",
    color: "text-yellow-400",
    icon: AlertCircle,
    label: "Waiting for Input",
  },
  completed: { bg: "bg-green-500/20", color: "text-green-400", icon: CheckCircle2, label: "Completed" },
  failed: { bg: "bg-red-500/20", color: "text-red-400", icon: AlertCircle, label: "Failed" },
  cancelled: { bg: "bg-surface-tertiary/20", color: "text-content-tertiary", icon: XCircle, label: "Cancelled" },
};

export function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskSummary | null>(null);
  const {
    position: menuAnchor,
    open: openMenu,
    close: closeMenu,
  } = useMenuPosition({ menuWidth: 160, menuHeight: 50 });
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create form state
  const [newGoal, setNewGoal] = useState("");
  const [newAgentUuid, setNewAgentUuid] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [sharepointFiles, setSharepointFiles] = useState<{ id: string; name: string; webUrl: string }[]>([]);
  const [isSharePointBrowserOpen, setIsSharePointBrowserOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const goalTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadTasks();
    loadAgents();
  }, []);

  // Consume prefill from location state (e.g. from Deep Research "Send to Autopilot")
  useEffect(() => {
    const state = location.state as { prefillGoal?: string } | null;
    if (state?.prefillGoal) {
      setNewGoal(state.prefillGoal);
      setShowCreateModal(true);
      // Clear location state so it doesn't re-apply on navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await taskService.list();
      setTasks(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tasks");
      console.error("Error loading tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await agentService.list({ pageSize: 100 });
      setAgents(response.data);
    } catch (err) {
      console.error("Error loading agents:", err);
    }
  };

  const handleDelete = async (taskUuid: string) => {
    const confirmed = await confirm({
      title: "Delete Task",
      message: "Are you sure you want to delete this task? This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      setActionLoading(taskUuid);
      await taskService.delete(taskUuid);
      await loadTasks();
      setSelectedTask(null);
    } catch (err) {
      console.error("Error deleting task:", err);
      showToast("Failed to delete task", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, task: TaskSummary) => {
    e.stopPropagation();
    if (selectedTask?.uuid === task.uuid) {
      setSelectedTask(null);
      closeMenu();
    } else {
      openMenu(e);
      setSelectedTask(task);
    }
  };

  const handleCreateTask = () => {
    setNewGoal("");
    setNewAgentUuid("");
    setUploadedFiles([]);
    setSharepointFiles([]);
    setShowCreateModal(true);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = "";
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const images = Array.from(e.target.files).filter((f) => ALLOWED_IMAGE_TYPES.has(f.type));
      setUploadedFiles((prev) => [...prev, ...images]);
    }
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitCreate = async () => {
    if (!newGoal.trim()) {
      showToast("Please describe what you want the agent to accomplish", "warning");
      return;
    }

    try {
      setCreating(true);
      const response = await taskService.create({
        goal: newGoal,
        agentUuid: newAgentUuid || undefined,
      });
      setShowCreateModal(false);
      setUploadedFiles([]);
      setSharepointFiles([]);
      // Navigate to the newly created task and auto-start execution
      navigate(`/tasks/${response.data.uuid}?autoStart=true`);
    } catch (err) {
      console.error("Error creating task:", err);
      showToast("Failed to create task", "error");
    } finally {
      setCreating(false);
    }
  };

  const filteredTasks = tasks.filter(
    (task) =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.goal.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={ListTodo}
        title="Autopilot"
        subtitle="Hand off a goal to an AI agent and let it plan, execute, and deliver autonomously"
        actions={
          <button
            onClick={handleCreateTask}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            New Goal
          </button>
        }
      >
        {/* Search */}
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
          <input
            type="text"
            placeholder="Search goals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          />
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
              </div>
            );
          }
          if (error) {
            return (
              <div className="flex flex-col items-center justify-center py-16">
                <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
                <h3 className="text-lg font-medium text-content-primary">Error loading goals</h3>
                <p className="text-content-tertiary mt-1">{error}</p>
                <button
                  onClick={loadTasks}
                  className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
                >
                  Try Again
                </button>
              </div>
            );
          }
          if (filteredTasks.length > 0) {
            return (
              <div className="space-y-4">
                {filteredTasks.map((task) => (
                  <div
                    key={task.uuid}
                    className="bg-surface-primary border border-border-primary rounded-xl p-5 hover:border-border-secondary transition-colors cursor-pointer"
                    role="button" // NOSONAR — contains nested interactive elements
                    tabIndex={0}
                    onClick={() => navigate(`/tasks/${task.uuid}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/tasks/${task.uuid}`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-medium text-content-primary">{task.title}</h3>
                          {(() => {
                            const cfg = statusConfig[task.status];
                            const StatusIcon = cfg.icon;
                            return (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                              >
                                <StatusIcon className={`h-3 w-3${task.status === "running" ? " animate-spin" : ""}`} />
                                {cfg.label}
                              </span>
                            );
                          })()}
                        </div>
                        <p className="text-content-tertiary text-sm mt-1 line-clamp-2">{task.goal}</p>

                        {/* Task metadata */}
                        <div className="mt-4 flex items-center gap-6 text-sm">
                          {task.agentName && (
                            <span className="flex items-center gap-1.5 text-content-quaternary">
                              <Bot className="h-3.5 w-3.5" />
                              <span className="text-content-tertiary">{task.agentName}</span>
                            </span>
                          )}
                          <span className="text-content-quaternary">
                            Steps: <span className="text-content-tertiary">{task.stepCount}</span>
                          </span>
                          <span className="text-content-quaternary">
                            Tokens: <span className="text-content-tertiary">{task.totalTokens.toLocaleString()}</span>
                          </span>
                          <span className="text-content-quaternary">
                            Created:{" "}
                            <span className="text-content-tertiary">
                              {new Date(task.createdAt).toLocaleDateString()}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 ml-4">
                        {task.status === "pending" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tasks/${task.uuid}`);
                            }}
                            className="p-2 text-content-tertiary hover:text-green-400 hover:bg-interactive-hover rounded-lg transition-colors"
                            title="Execute task"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                        )}
                        <div className="relative">
                          <button
                            onClick={(e) => handleMenuToggle(e, task)}
                            className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
                            disabled={actionLoading === task.uuid}
                          >
                            {actionLoading === task.uuid ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreHorizontal className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center mb-4">
                <ListTodo className="h-8 w-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-medium text-content-primary">No goals yet</h3>
              <p className="text-content-tertiary mt-1 text-center max-w-md">
                {searchQuery
                  ? "Try adjusting your search query"
                  : "Autopilot lets you hand off a complex goal to an AI agent. It will plan the steps, execute them autonomously, and check back if it needs your input."}
              </p>
              {!searchQuery && (
                <>
                  <div className="mt-5 flex flex-col gap-2 text-sm text-content-quaternary max-w-md w-full">
                    <p className="text-xs font-semibold uppercase tracking-wider text-content-quaternary mb-1">
                      Example goals
                    </p>
                    <div className="bg-surface-primary/50 border border-border-primary rounded-lg px-4 py-2.5 text-content-tertiary">
                      "Research the top 5 competitors in our space and produce a comparison matrix of features, pricing,
                      and market positioning"
                    </div>
                    <div className="bg-surface-primary/50 border border-border-primary rounded-lg px-4 py-2.5 text-content-tertiary">
                      "Analyze last quarter's support tickets, identify the top recurring issues, and draft an action
                      plan to reduce ticket volume"
                    </div>
                    <div className="bg-surface-primary/50 border border-border-primary rounded-lg px-4 py-2.5 text-content-tertiary">
                      "Write a technical RFC for migrating our auth system to OAuth 2.0, including risks, timeline, and
                      rollback strategy"
                    </div>
                  </div>
                  <button
                    onClick={handleCreateTask}
                    className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                  >
                    <Plus className="h-4 w-4" />
                    New Goal
                  </button>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">Create New Goal</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Example prompt chips */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-content-quaternary mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" />
                  Try an example
                </p>
                <div className="flex flex-col gap-2">
                  {EXAMPLE_GOALS.map((example) => (
                    <button
                      key={example}
                      onClick={() => {
                        setNewGoal(example);
                        goalTextareaRef.current?.focus();
                      }}
                      className="text-left text-sm bg-surface-secondary/50 border border-border-secondary rounded-lg px-3 py-2 text-content-tertiary hover:text-content-secondary hover:border-border-hover hover:bg-surface-secondary transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>

              {/* Uploaded file previews */}
              {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {uploadedFiles.map((file, index) => {
                    const isImage = ALLOWED_IMAGE_TYPES.has(file.type);
                    return (
                      <div key={`file-${file.name}-${index}`} className="relative group">
                        {isImage ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-16 h-16 rounded-lg object-cover border border-border-secondary"
                          />
                        ) : (
                          <div className="flex items-center gap-2 bg-surface-secondary pl-3 pr-2 py-2 rounded-lg border border-border-secondary">
                            <Paperclip className="w-3.5 h-3.5 text-content-tertiary shrink-0" />
                            <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                          </div>
                        )}
                        <button
                          onClick={() => removeFile(index)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-surface-tertiary hover:bg-red-500 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* SharePoint file chips */}
              {sharepointFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {sharepointFiles.map((file, index) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-2 py-2 rounded-lg"
                    >
                      <Cloud className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                      <button
                        onClick={() =>
                          setSharepointFiles(
                            (prev) => prev.filter((_, i) => i !== index), // NOSONAR — unused _ param; only index needed for positional removal
                          )
                        }
                        className="p-1 hover:bg-interactive-active rounded-sm transition"
                      >
                        <X className="w-3 h-3 text-content-tertiary" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input bar with buttons + textarea */}
              <div className="flex gap-3">
                {/* File attachment button */}
                <label className="flex items-center justify-center w-10 h-10 bg-surface-secondary hover:bg-interactive-active rounded-lg cursor-pointer transition border border-border-secondary shrink-0 self-end">
                  <Paperclip className="w-5 h-5 text-content-tertiary" />
                  <input ref={fileInputRef} type="file" multiple onChange={handleFileUpload} className="hidden" />
                </label>
                {/* Image upload button */}
                <label className="flex items-center justify-center w-10 h-10 bg-surface-secondary hover:bg-interactive-active rounded-lg cursor-pointer transition border border-border-secondary shrink-0 self-end">
                  <ImageIcon className="w-5 h-5 text-content-tertiary" />
                  <input
                    ref={imageInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
                {/* SharePoint button */}
                <button
                  onClick={() => setIsSharePointBrowserOpen(true)}
                  className="flex items-center justify-center w-10 h-10 bg-surface-secondary hover:bg-interactive-active rounded-lg transition border border-border-secondary shrink-0 self-end"
                  title={getRuntimeConfig().authProvider === "google" ? "Add from Drive" : "Add from SharePoint"}
                >
                  <Cloud className="w-5 h-5 text-content-tertiary" />
                </button>
                {/* Voice input */}
                <div className="shrink-0 self-end">
                  <VoiceInputButton
                    onTranscript={(text) => setNewGoal((prev) => (prev ? `${prev} ${text}` : text))}
                    disabled={creating}
                  />
                </div>
                {/* Goal textarea */}
                <textarea
                  ref={goalTextareaRef}
                  rows={3}
                  placeholder="Describe an end goal, not step-by-step instructions..."
                  value={newGoal}
                  onChange={(e) => setNewGoal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitCreate();
                    }
                  }}
                  className="flex-1 bg-surface-secondary text-content-primary px-4 py-3 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary placeholder-content-quaternary resize-none"
                  autoFocus
                />
                {/* Send / Start button */}
                <div className="shrink-0 self-end">
                  <button
                    onClick={handleSubmitCreate}
                    disabled={creating || !newGoal.trim()}
                    className="flex items-center justify-center w-10 h-10 bg-brand-gradient hover:opacity-90 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Start Goal"
                  >
                    {creating ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Send className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-xs text-content-quaternary">
                Describe the outcome you need. The agent will break it into steps and execute them on its own. Press
                Enter to start.
              </p>

              {/* Agent dropdown */}
              <div>
                <label htmlFor="task-agent-select" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Agent (optional)
                </label>
                <select
                  id="task-agent-select"
                  value={newAgentUuid}
                  onChange={(e) => setNewAgentUuid(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                >
                  <option value="">Use default agent</option>
                  {agents.map((agent) => (
                    <option key={agent.uuid} value={agent.uuid}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-content-quaternary mt-1">
                  Choose an agent to execute this task, or leave blank for the default.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitCreate}
                disabled={creating || !newGoal.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Start Goal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cloud Storage Browser Modal */}
      {isSharePointBrowserOpen && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-60 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            {getRuntimeConfig().authProvider === "google" ? (
              <GoogleDriveBrowser
                onSelect={(selection) => {
                  const newFiles = selection.items.map((item) => ({
                    id: item.id,
                    name: item.name,
                    webUrl: item.web_url,
                  }));
                  setSharepointFiles((prev) => [...prev, ...newFiles]);
                  setIsSharePointBrowserOpen(false);
                }}
                onCancel={() => setIsSharePointBrowserOpen(false)}
              />
            ) : (
              <SharePointBrowser
                onSelect={(selection) => {
                  const newFiles = selection.items.map((item) => ({
                    id: item.id,
                    name: item.name,
                    webUrl: item.web_url,
                  }));
                  setSharepointFiles((prev) => [...prev, ...newFiles]);
                  setIsSharePointBrowserOpen(false);
                }}
                onCancel={() => setIsSharePointBrowserOpen(false)}
                allowOneDrive
              />
            )}
          </div>
        </div>
      )}

      {/* Backdrop + anchored dropdown to close task menu when clicking outside */}
      {selectedTask && menuAnchor && (
        <div
          className="fixed inset-0 z-40"
          role="presentation" // NOSONAR — decorative backdrop element
          onClick={() => {
            setSelectedTask(null);
            closeMenu();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSelectedTask(null);
              closeMenu();
            }
          }}
        >
          <div
            style={{ position: "fixed", top: `${menuAnchor.top}px`, left: `${menuAnchor.left}px` }}
            className="bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 min-w-40 max-w-[clamp(10rem,25vw,20rem)]"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => handleDelete(selectedTask.uuid)}
              disabled={actionLoading === selectedTask.uuid}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-interactive-hover transition-colors disabled:opacity-50"
            >
              {actionLoading === selectedTask.uuid ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
